import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, of, withLatestFrom, tap, filter } from 'rxjs';
import { PigService } from '../../services/pig-service.service';

import * as fromPigs from '../actions/pig.actions';
import * as fromSelectors from '../selectors/pig.selectors';
import * as fromReducer from '../reducers/pig.reducer';
import * as fromErrors from '../fromErrors.barrel';
import { Store } from '@ngrx/store';
import { Logger } from '../../util/logger';
import { MonitoringService } from '../../services/monitoring.service';

@Injectable()
export class PigEffects {
  constructor(
    private actions$: Actions,
    private pigService: PigService,
    private store: Store<fromReducer.State>,
    private logger: Logger,
    private monitoringService: MonitoringService) { }

  loadLookups$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigs.loadPigLookups),
    withLatestFrom(this.store.select(fromSelectors.selectHasLoadedLookups)),
    filter(([action, alreadyLoaded]) => !!action.force || !alreadyLoaded),
    switchMap(action => this.pigService.getLookups()
      .pipe(map(data => fromPigs.loadPigLookupsSuccess({ data })),
        catchError(error => of(fromPigs.loadPigLookupsFailure({ error })))))
  ));

  formChanges$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromPigs.changePreviouslyPigged,
      fromPigs.changePipeSizeId,
      fromPigs.changePurposeId,
      fromPigs.changeServiceId,
      fromPigs.changeBidirectional,
      fromPigs.changeEfficiency,
      fromPigs.changeAggressiveness),
    map(action => fromPigs.loadPigQueryResults())));

  loadQueryResults$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigs.loadPigQueryResults),
    withLatestFrom(
      this.store.select(fromSelectors.selectFilterQuerystring),
      this.store.select(fromSelectors.selectShouldCallHttpForFilter)),
    tap(([action, querystring, shouldMakeHttpCall]) => this.logger.log(shouldMakeHttpCall)),
    filter(([action, querystring, shouldMakeHttpCall]) => !!shouldMakeHttpCall),
    switchMap(([action, querystring, shouldMakeHttpCall]) => this.pigService.getQueryResults(querystring)
      .pipe(map(res => fromPigs.loadPigQueryResultsSuccess({ results: res })),
        catchError(error => of(fromPigs.loadPigQueryResultsFailure({ error }))))
    )
  ));

  loadRefsOnActivatedPig$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigs.activatePig),
    map(action => fromPigs.loadActivatedPigReferences())
  ));

  loadPigDetail$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigs.loadActivatedPigReferences),
    withLatestFrom(
      this.store.select(fromSelectors.selectActiveId),
      this.store.select(fromSelectors.selectActiveObj),
      this.store.select(fromSelectors.selectActivePurposeId)
    ),
    switchMap(([action, id, obj, purposeId]) => this.pigService.getPigDetailRefs(id || -1, obj?.partNumber || '', obj?.familyId || -1, purposeId || -1)
      .pipe(map(res => fromPigs.loadActivatedPigReferencesSuccess(res)),
        catchError(error => of(fromPigs.loadActivatedPigReferencesFail({ error })))))));

  downloadActivePigCutSheet$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigs.loadActivePigCutSheet),
    withLatestFrom(this.store.select(fromSelectors.selectActiveId)),
    switchMap(([action, id]) => this.pigService.getPigCutSheet(id || -1)
      .pipe(map(file => {
        window.open(URL.createObjectURL(file));
        return fromPigs.loadActivePigCutSheetSuccess();
      }))),
    catchError(error => of(fromPigs.loadActivatedPigReferencesFail({ error })))
  ));

  monitorErrors$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromPigs.loadPigLookupsFailure,
      fromPigs.loadPigQueryResultsFailure,
      fromPigs.loadActivePigCutSheetFailure,
      fromPigs.loadActivatedPigReferencesFail),
    tap(error => this.monitoringService.logException(error.error)),
    map(error => fromErrors.addGlobalError({ error: error.error }))
  ));
}
